import classNames from "classnames";
import React, { useEffect, useState } from "react";
import { isEmpty } from "../../common/utils/string.util";
import "../LoadingSpinner/LoadingSpinner.css";
import "./EditableInputField.css";
import LoadingSpinner from "../LoadingSpinner/LoadingSpinner";
import InternalIndicator from "./InternalIndicator";

type Props = {
    type?: string;
    label?: string;
    placeholder?: string;
    autoComplete?: "on" | "off";
    initialValue?: string | null;
    className?: string;
    direction?: "row" | "column";
    internalIndicator?: boolean;
    additionalLinkLabel?: string | undefined | null;
    additionalLinkUrl?: string | undefined | null;
    onChange?: (value: string) => Promise<any> | void;
    onClick?: () => void;
    validate?: (value: string) => boolean;
    autoResize?: boolean;
    allowEmpty?: boolean;
    readOnly?: boolean;
    disabled?: boolean;
    triggerMultipleOnChange?: boolean;
    trim?: boolean;
    triggerOnChangeOnInvalidation?: boolean;
    highlight?: boolean;
};

const EditableInputField = function EditableInputField(props: Props) {
    const {
        type,
        label,
        placeholder,
        initialValue,
        className,
        direction,
        internalIndicator,
        additionalLinkLabel,
        additionalLinkUrl,
        autoResize,
        allowEmpty,
        autoComplete,
        readOnly,
        disabled,
        triggerMultipleOnChange,
        trim,
        triggerOnChangeOnInvalidation,
        validate,
        onChange,
        onClick,
        highlight,
    } = props;
    const [value, setValue] = useState<string>(initialValue || "");
    const [isFocused, setIsFocused] = useState(false);
    const [hasBeenEdited, setHasBeenEdited] = useState<boolean>(false);
    const [isUpdating, setIsUpdating] = useState(false);

    const isValid = () => {
        if (validate) {
            return validate(value);
        }

        return true;
    };

    const triggerChanged = async () => {
        const dispatchValue = triggerOnChangeOnInvalidation ? true : isValid();

        if (
            hasBeenEdited &&
            onChange &&
            (allowEmpty || value.length > 0) &&
            dispatchValue
        ) {
            try {
                setIsUpdating(true);
                await onChange(value);
            } catch {
                setValue(initialValue || "");
            }
            setIsUpdating(false);
            setIsFocused(false);
            setHasBeenEdited(false);
        }
        if (!allowEmpty && value.length === 0) {
            setValue(initialValue || "");
        }
    };

    useEffect(() => {
        if (triggerMultipleOnChange && !isEmpty(value)) triggerChanged();
    }, [value]);

    const size = autoResize
        ? (isFocused ? value || placeholder || "" : initialValue || "").length + 1
        : undefined;

    useEffect(() => {
        setValue(initialValue || "");
    }, [initialValue]);

    return (
        <div
            className={classNames(
                "editable-textfield",
                className,
                `editable-textfield--${direction}`,
            )}
        >
            {label ? (
                <label className="editable-textfield__label">
                    {label}
                    {internalIndicator && <InternalIndicator />}
                </label>
            ) : (
                ""
            )}
            <div className="editable-textfield__input-container">
                {isUpdating && <LoadingSpinner.Medium.AbsoluteTopRight />}
                <input
                    className={classNames({
                        "editable-textfield__input": true,
                        "invalid-input": !isValid(),
                        "editable-textfield__input-readonly": readOnly ?? false,
                        "!border-blue-200 !bg-blue-50": highlight,
                    })}
                    type={type || "text"}
                    placeholder={placeholder || "Click to add..."}
                    value={value}
                    size={size}
                    autoComplete={autoComplete}
                    onClick={onClick}
                    onFocus={() => {
                        setIsFocused(true);
                    }}
                    onBlur={() => {
                        triggerChanged();
                    }}
                    onKeyDown={(event) => {
                        if (event.key === "Enter") event.currentTarget.blur();
                    }}
                    onChange={(e) => {
                        setHasBeenEdited(true);
                        setValue(trim ? e.target.value.trim() : e.target.value);
                    }}
                    readOnly={readOnly}
                    disabled={disabled}
                />
            </div>
            {additionalLinkLabel && additionalLinkUrl && (
                <a
                    href={additionalLinkUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="editable-textfield__link"
                >
                    {additionalLinkLabel}
                </a>
            )}
        </div>
    );
};

export default EditableInputField;
